import { FormEvent, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import isEmail from 'validator/lib/isEmail'
import { Button, Header, TextInput } from 'components/common'
import { axiosBase } from 'utils/functions'
import PublicPageContainer from './PublicPageContainer'
import './AuthPages.scss'

type LoginStep = 'email' | 'password'

function LoginPage() {
  const { t } = useTranslation('pub', {
    keyPrefix: 'PublicRoutes.auth',
  })
  const location = useLocation()
  const unlockSuccess = (location.state as any)?.unlockSuccess
  const [step, setStep] = useState<LoginStep>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleEmailSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!isEmail(email)) {
      setError(t('login.invalid'))
      return
    }

    setError('')
    setStep('password')
  }

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      await axiosBase.post('/oauth/login-cookie', {
        username: email,
        password,
      })

      window.location.assign('/')
    } catch (_error) {
      setError(t('login.invalid'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditEmail = () => {
    setStep('email')
    setPassword('')
    setError('')
  }

  return (
    <PublicPageContainer>
      <div className="auth-page">
        <div className="auth-card">
          <Header as="h1" className="auth-title">
            {t('all.welcome')}
          </Header>
          <div className="auth-subtitle">{t('all.welcomeDesc')}</div>

          {step === 'email' ? (
            <form className="auth-form" onSubmit={handleEmailSubmit} noValidate>
              <TextInput
                id="login-email"
                type="email"
                label={t('all.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                fullBorder
                error={error}
              />

              <Button color="blue" type="submit" loading={isSubmitting}>
                {t('all.continue')}
              </Button>
            </form>
          ) : (
            <form
              className="auth-form"
              onSubmit={handlePasswordSubmit}
              noValidate
            >
              <TextInput
                id="login-password"
                type="password"
                label={t('all.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                fullBorder
                error={error}
              />

              <Button color="blue" type="submit" loading={isSubmitting}>
                {t('all.signIn')}
              </Button>
            </form>
          )}

          <div className="auth-link">
            <Link className="auth-link" to="/unlock-instructions">
              {t('login.missingUnlock')}
            </Link>
            <Link className="auth-link" to="/forgot-password">
              {t('all.forgotPassword')}
            </Link>
            {step === 'password' ? (
              <span className="auth-email-display">
                {email}{' '}
                <button
                  className="auth-link-button"
                  onClick={handleEditEmail}
                  type="button"
                >
                  {t('login.edit')}
                </button>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </PublicPageContainer>
  )
}

export default LoginPage
